import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Paperclip, X, Search } from "lucide-react";
import api from "../../api/api";
import "../../App.css";

export default function Attachments({ mdlId, primeId, primeName, uploadedBy }) {
  const [attachments, setAttachments] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [remarks, setRemarks] = useState("");

  const modalRef = useRef(null);

  // ✅ Fetch attachments immediately when mdlId or primeId changes
  useEffect(() => {
    if (mdlId && primeId) fetchFiles();
  }, [mdlId, primeId]);

  // ✅ Also re-fetch when modal opens (no logic removed)
  useEffect(() => {
    if (isOpen && mdlId && primeId) fetchFiles();
  }, [isOpen, mdlId, primeId]);

  const fetchFiles = async () => {
    if (!mdlId || !primeId) return;
    try {
      const res = await api.GetAttachments(mdlId, primeId);
      const table = res?.Attachments?.Table || res?.Attachments || [];
      setAttachments(Array.isArray(table) ? table : []);
    } catch (err) {
      console.error("❌ Error loading attachments:", err);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(files);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      alert("Please select files first!");
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append("MdlId", mdlId);
    formData.append("PrimeId", primeId);
    formData.append("PrimeName", primeName);
    formData.append("UploadedBy", uploadedBy);
    formData.append("Remarks", remarks);
    selectedFiles.forEach((file) => formData.append("files", file));

    try {
      const res = await api.UploadAttachment(formData);
      if (res?.success) {
        setSelectedFiles([]);
        setRemarks("");
        await fetchFiles(); // ✅ Update count right after upload
      } else {
        alert("⚠️ Upload failed. Please try again.");
      }
    } catch (err) {
      console.error("❌ Upload failed:", err);
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    const handleEsc = (e) => e.key === "Escape" && setIsOpen(false);
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  const filteredAttachments = attachments.filter((file) =>
    (file.FileName || "").toLowerCase().includes(searchTerm.trim().toLowerCase())
  );

  return (
    <>
      {/* 📎 Attachments Button */}
      <button type="button" onClick={() => setIsOpen(true)} className="btn-attachments">
        <Paperclip size={18} />
        <span>
          Attachments
          {attachments.length > 0 && (
            <span className="badge-count">{attachments.length}</span>
          )}
        </span>
      </button>

      {/* 🧩 Modal */}
      {isOpen &&
        ReactDOM.createPortal(
          <AnimatePresence>
            <>
              <motion.div
                className="modal-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsOpen(false)}
              />

              <motion.div
                ref={modalRef}
                className="modal-container"
                initial={{ opacity: 0, scale: 0.9, x: "-50%", y: "-50%" }}
                animate={{ opacity: 1, scale: 1, x: "-50%", y: "-50%" }}
                exit={{ opacity: 0, scale: 0.9, x: "-50%", y: "-50%" }}
                transition={{ type: "spring", stiffness: 250, damping: 22 }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="modal-header">
                  <h2>
                    <div className="modal-title">
                      <Paperclip size={22} /> Attachments
                    </div>
                    <div className="modal-subtitle">
                      <span>{primeName}</span>
                      <span className="file-count">({attachments.length})</span>
                    </div>
                  </h2>
                  <button onClick={() => setIsOpen(false)} className="btn-close">
                    <X size={22} />
                  </button>
                </div>

                {/* Upload Section */}
                <div className="upload-section">
                  <div className="upload-row">
                    <input type="file" multiple onChange={handleFileSelect} />
                    <button
                      onClick={handleUpload}
                      disabled={isUploading}
                      className={`btn-upload ${isUploading ? "btn-disabled" : ""}`}
                    >
                      {isUploading ? "Uploading..." : "Upload"}
                    </button>
                  </div>

                  <textarea
                    placeholder="Add remarks (optional)..."
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    rows="2"
                  />
                  {selectedFiles.length > 0 && (
                    <p className="file-selected">
                      {selectedFiles.length} file(s) selected
                    </p>
                  )}
                </div>

                {/* Search Bar */}
                <div className="search-bar">
                  <Search size={18} />
                  <input
                    type="text"
                    placeholder="Search files..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                {/* File List */}
                <div className="file-list">
                  {filteredAttachments.length === 0 ? (
                    <p className="no-files">No matching files found.</p>
                  ) : (
                    <ul>
                      {filteredAttachments.map((file, index) => {
                        const name =
                          file.FileName ||
                          file.File_Path ||
                          file.Filename ||
                          file.DocName ||
                          file.OriginalFileName ||
                          "Unnamed File";
                        const path = file.FilePath || file.File_Path || "#";
                        const remarks = file.Remarks || "—";
                        return (
                          <li key={index} className="file-item">
                            <div className="file-top">
                              <div className="file-info">
                                <a
                                  href={path}
                                  target="_blank"
                                  rel="noreferrer"
                                  title={name}
                                >
                                  {name}
                                </a>
                              </div>
                              <span className="upload-date">
                                {file.UploadedDate
                                  ? new Date(file.UploadedDate).toLocaleDateString()
                                  : ""}
                              </span>
                            </div>
                            {remarks && remarks !== "—" && (
                              <div className="file-remarks">
                                📝 <span>{remarks}</span>
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </motion.div>
            </>
          </AnimatePresence>,
          document.body
        )}
    </>
  );
}




// import React, { useState, useEffect, useRef } from "react";
// import ReactDOM from "react-dom";
// import { motion, AnimatePresence } from "framer-motion";
// import { Paperclip, X, Search } from "lucide-react";
// import api from "../../api/api";
// import "../../App.css";


// export default function Attachments({ mdlId, primeId, primeName, uploadedBy }) {
//   const [attachments, setAttachments] = useState([]);
//   const [selectedFiles, setSelectedFiles] = useState([]);
//   const [isUploading, setIsUploading] = useState(false);
//   const [isOpen, setIsOpen] = useState(false);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [remarks, setRemarks] = useState("");

//   const modalRef = useRef(null);

//   useEffect(() => {
//     if (isOpen && mdlId && primeId) fetchFiles();
//   }, [isOpen, mdlId, primeId]);

//   const fetchFiles = async () => {
//     if (!mdlId || !primeId) return;
//     try {
//       const res = await api.GetAttachments(mdlId, primeId);
//       const table = res?.Attachments?.Table || res?.Attachments || [];
//       setAttachments(Array.isArray(table) ? table : []);
//     } catch (err) {
//       console.error("❌ Error loading attachments:", err);
//     }
//   };

//   const handleFileSelect = (e) => {
//     const files = Array.from(e.target.files || []);
//     setSelectedFiles(files);
//   };

//   const handleUpload = async () => {
//     if (selectedFiles.length === 0) {
//       alert("Please select files first!");
//       return;
//     }

//     setIsUploading(true);
//     const formData = new FormData();
//     formData.append("MdlId", mdlId);
//     formData.append("PrimeId", primeId);
//     formData.append("PrimeName", primeName);
//     formData.append("UploadedBy", uploadedBy);
//     formData.append("Remarks", remarks);
//     selectedFiles.forEach((file) => formData.append("files", file));

//     try {
//       const res = await api.UploadAttachment(formData);
//       if (res?.success) {
//         setSelectedFiles([]);
//         setRemarks("");
//         await fetchFiles();
//       } else alert("⚠️ Upload failed. Please try again.");
//     } catch (err) {
//       console.error("❌ Upload failed:", err);
//     } finally {
//       setIsUploading(false);
//     }
//   };

//   useEffect(() => {
//     const handleEsc = (e) => e.key === "Escape" && setIsOpen(false);
//     window.addEventListener("keydown", handleEsc);
//     return () => window.removeEventListener("keydown", handleEsc);
//   }, []);

//   const filteredAttachments = attachments.filter((file) =>
//     (file.FileName || "").toLowerCase().includes(searchTerm.trim().toLowerCase())
//   );

//   return (
//     <>
//       {/* 📎 Attachments Button */}
//       <button onClick={() => setIsOpen(true)} className="btn-attachments">
//         <Paperclip size={18} />
//         <span>
//           Attachments
//           {attachments.length > 0 && (
//             <span className="badge-count">{attachments.length}</span>
//           )}
//         </span>
//       </button>

//       {/* 🧩 Modal */}
//       {isOpen &&
//         ReactDOM.createPortal(
//           <AnimatePresence>
//             <>
//               <motion.div
//                 className="modal-overlay"
//                 initial={{ opacity: 0 }}
//                 animate={{ opacity: 1 }}
//                 exit={{ opacity: 0 }}
//                 onClick={() => setIsOpen(false)}
//               />

//               <motion.div
//                 ref={modalRef}
//                 className="modal-container"
//                 initial={{ opacity: 0, scale: 0.9, x: "-50%", y: "-50%" }}
//                 animate={{ opacity: 1, scale: 1, x: "-50%", y: "-50%" }}
//                 exit={{ opacity: 0, scale: 0.9, x: "-50%", y: "-50%" }}
//                 transition={{ type: "spring", stiffness: 250, damping: 22 }}
//                 onClick={(e) => e.stopPropagation()}
//               >
//               {/* Header */}
//               <div className="modal-header">
//                 <h2>
//                   <div className="modal-title">
//                     <Paperclip size={22} /> Attachments
//                   </div>
//                   <div className="modal-subtitle">
//                     <span>{primeName}</span>
//                     <span className="file-count">({attachments.length})</span>
//                   </div>
//                 </h2>
//                 <button onClick={() => setIsOpen(false)} className="btn-close">
//                   <X size={22} />
//                 </button>
//               </div>

//               {/* Upload Section */}
//               <div className="upload-section">
//                 <div className="upload-row">
//                   <input type="file" multiple onChange={handleFileSelect} />
//                   <button
//                     onClick={handleUpload}
//                     disabled={isUploading}
//                     className={`btn-upload ${
//                       isUploading ? "btn-disabled" : ""
//                     }`}
//                   >
//                     {isUploading ? "Uploading..." : "Upload"}
//                   </button>
//                 </div>

//                 <textarea
//                   placeholder="Add remarks (optional)..."
//                   value={remarks}
//                   onChange={(e) => setRemarks(e.target.value)}
//                   rows="2"
//                 />
//                 {selectedFiles.length > 0 && (
//                   <p className="file-selected">
//                     {selectedFiles.length} file(s) selected
//                   </p>
//                 )}
//               </div>

//               {/* Search Bar */}
//               <div className="search-bar">
//                 <Search size={18} />
//                 <input
//                   type="text"
//                   placeholder="Search files..."
//                   value={searchTerm}
//                   onChange={(e) => setSearchTerm(e.target.value)}
//                 />
//               </div>

//               {/* File List */}
//               <div className="file-list">
//                 {filteredAttachments.length === 0 ? (
//                   <p className="no-files">No matching files found.</p>
//                 ) : (
//                   <ul>
//                     {filteredAttachments.map((file, index) => {
//                       const name =
//                         file.FileName ||
//                         file.File_Path ||
//                         file.Filename ||
//                         file.DocName ||
//                         file.OriginalFileName ||
//                         "Unnamed File";
//                       const path = file.FilePath || file.File_Path || "#";
//                       const remarks = file.Remarks || "—";
//                       return (
//                         <li key={index} className="file-item">
//                           <div className="file-top">
//                             <div className="file-info">
//                               <a
//                                 href={path}
//                                 target="_blank"
//                                 rel="noreferrer"
//                                 title={name}
//                               >
//                                 {name}
//                               </a>
//                             </div>
//                             <span className="upload-date">
//                               {file.UploadedDate
//                                 ? new Date(
//                                     file.UploadedDate
//                                   ).toLocaleDateString()
//                                 : ""}
//                             </span>
//                           </div>
//                           {remarks && remarks !== "—" && (
//                             <div className="file-remarks">
//                               📝 <span>{remarks}</span>
//                             </div>
//                           )}
//                         </li>
//                       );
//                     })}
//                   </ul>
//                 )}
//               </div>
//             </motion.div>
//           </>
//         </AnimatePresence>,
//         document.body
//       )}
//     </>
//   );
// }
